import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CameraIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

const CompanionAvatar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  const [uploadedImage, setUploadedImage] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultAvatars = [
    { id: '1', emoji: '🌟', bg: 'bg-purple-500' },
    { id: '2', emoji: '🌙', bg: 'bg-blue-500' },
    { id: '3', emoji: '🌸', bg: 'bg-pink-500' },
    { id: '4', emoji: '🍀', bg: 'bg-green-500' },
    { id: '5', emoji: '🔮', bg: 'bg-indigo-500' },
    { id: '6', emoji: '🦋', bg: 'bg-teal-500' },
    { id: '7', emoji: '🌈', bg: 'bg-yellow-500' },
    { id: '8', emoji: '⚡', bg: 'bg-orange-500' },
  ];

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert(
        t(
          'companionCreation.invalidFileType',
          'Please select a valid image file (JPEG, PNG, GIF, or WebP)',
        ),
      );
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      alert(t('companionCreation.fileTooLarge', 'File size must be less than 5MB'));
      return;
    }

    setIsUploading(true);

    try {
      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      setUploadedImage(previewUrl);
      setUploadedFile(file);
      setSelectedAvatar(''); // Clear emoji selection

      // Store as base64 data URL so it survives navigation (blob URLs get revoked)
      const reader = new FileReader();
      reader.onloadend = () => {
        sessionStorage.setItem('avatarPreview', reader.result as string);
      };
      reader.readAsDataURL(file);
      sessionStorage.setItem('avatarFileName', file.name);
    } catch (error) {
      console.error('Error handling file upload:', error);
      alert(t('companionCreation.uploadError', 'Error uploading file. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Cleanup object URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (uploadedImage) {
        URL.revokeObjectURL(uploadedImage);
      }
    };
  }, [uploadedImage]);

  const handleContinue = async () => {
    // Collect all companion data
    const companionData = {
      name:
        sessionStorage.getItem('companionName') || t('companionCreation.defaultName', 'Companion'),
      gender: sessionStorage.getItem('companionGender') || 'neutral',
      relationshipType: sessionStorage.getItem('companionRelationshipType') || 'romantic',
      personality:
        sessionStorage.getItem('companionPersonality') ||
        t('companionCreation.defaultPersonality', 'A thoughtful and caring companion'),
      avatarUrl: uploadedImage || selectedAvatar || defaultAvatars[0].id,
      hasCustomAvatar: !!uploadedImage,
    };

    // Store complete data (will be sent to API)
    sessionStorage.setItem('companionData', JSON.stringify(companionData));

    // Store the file globally for access in CompanionReady
    if (uploadedFile) {
      (window as any).companionAvatarFile = uploadedFile;
    }

    // Navigate to companion ready screen or main app
    navigate('/companion/ready');
  };

  const handleSkip = () => {
    setSelectedAvatar(defaultAvatars[0].id);
    handleContinue();
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <div className="pt-safe px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Progress indicator - 6 steps */}
          <div className="flex space-x-2 mb-8">
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
            {t('companionCreation.avatarTitle', 'Choose an avatar for your companion')}
          </h1>
          <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-8">
            {t(
              'companionCreation.avatarSubtitle',
              'Pick something that represents their personality',
            )}
          </p>

          {/* Avatar options */}
          <div className="mb-8">
            {/* Upload/Generate options */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={handleUploadClick}
                disabled={isUploading}
                className="p-4 bg-dark-surface dark:bg-dark-surface bg-light-surface rounded-2xl border-2 border-dark-border dark:border-dark-border border-light-border hover:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CameraIcon className="w-6 h-6 text-primary-500 mx-auto mb-2" />
                <p className="text-sm text-dark-text dark:text-dark-text text-light-text">
                  {isUploading
                    ? t('companionCreation.uploading', 'Uploading...')
                    : t('companionCreation.uploadPhoto', 'Upload Photo')}
                </p>
                <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mt-1">
                  {t('companionCreation.maxFileSize', 'Max 5MB')}
                </p>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                disabled
                className="p-4 bg-dark-surface dark:bg-dark-surface bg-light-surface rounded-2xl border-2 border-dark-border dark:border-dark-border border-light-border opacity-50 cursor-not-allowed"
              >
                <SparklesIcon className="w-6 h-6 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mx-auto mb-2" />
                <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                  {t('companionCreation.generateAI', 'Generate AI')}
                </p>
                <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mt-1">
                  {t('companionCreation.comingSoon', '(Coming soon)')}
                </p>
              </button>
            </div>

            {/* Default avatars */}
            <div>
              <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-3">
                {t('companionCreation.chooseDefaultAvatar', 'Choose a default avatar:')}
              </p>
              <div className="grid grid-cols-4 gap-3">
                {defaultAvatars.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => setSelectedAvatar(avatar.id)}
                    className={`aspect-square rounded-2xl flex items-center justify-center text-3xl transition-all ${
                      selectedAvatar === avatar.id
                        ? 'ring-4 ring-primary-500 ring-offset-4 ring-offset-dark-bg dark:ring-offset-dark-bg ring-offset-white'
                        : 'hover:scale-105'
                    } ${avatar.bg}`}
                  >
                    {avatar.emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          {(uploadedImage || selectedAvatar) && (
            <div className="mb-auto">
              <div className="bg-dark-surface dark:bg-dark-surface bg-light-surface rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  {uploadedImage ? (
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-200">
                      <img
                        src={uploadedImage}
                        alt="Uploaded avatar"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                        defaultAvatars.find((a) => a.id === selectedAvatar)?.bg
                      }`}
                    >
                      {defaultAvatars.find((a) => a.id === selectedAvatar)?.emoji}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-dark-text dark:text-dark-text text-light-text">
                      {sessionStorage.getItem('companionName') ||
                        t('companionCreation.defaultName', 'Companion')}
                    </p>
                    <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                      {uploadedImage
                        ? t('companionCreation.customAvatar', 'Custom avatar')
                        : t('companionCreation.yourNewCompanion', 'Your new companion')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleContinue}
              disabled={!selectedAvatar && !uploadedImage}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('companionCreation.completeSetup', 'Complete Setup')}
            </button>
            <button onClick={handleSkip} className="btn-secondary">
              {t('companionCreation.useDefaultAvatar', 'Use default avatar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionAvatar;
